import { useMemo } from 'react';
import { Select } from 'antd';
import type { FindingGroup, FindingLabel } from '@/models/template';
import { humanizeClass } from '../primitives/format';
import styles from './EditableFindingsSection.module.css';

interface EditableGroupedFindingBlockProps {
  group: FindingGroup;
  findingClasses: string[];
  usedClasses: Set<string>;
  onChange: (group: FindingGroup) => void;
}

export function EditableGroupedFindingBlock({
  group,
  findingClasses,
  usedClasses,
  onChange,
}: EditableGroupedFindingBlockProps) {
  const selectedClasses = useMemo(
    () =>
      [...group.findingLabels]
        .sort((a, b) => a.index - b.index)
        .map((fl) => fl.findingClass)
        .filter(Boolean),
    [group.findingLabels]
  );

  // Classes already used in OTHER groups — hide from our picker to prevent
  // duplicates. Classes used in THIS group remain (we need to show them).
  const otherGroupClasses = useMemo(() => {
    const ours = new Set(selectedClasses);
    const filtered = new Set<string>();
    usedClasses.forEach((c) => {
      if (!ours.has(c)) filtered.add(c);
    });
    return filtered;
  }, [usedClasses, selectedClasses]);

  const options = useMemo(
    () =>
      findingClasses
        .filter((c) => !otherGroupClasses.has(c))
        .map((c) => ({ value: c, label: humanizeClass(c) })),
    [findingClasses, otherGroupClasses]
  );

  const handleClassesChange = (next: string[]) => {
    // Diff against existing findingLabels: keep ones still selected (by
    // class), add new ones for newly-picked classes. Preserve order from
    // the multi-select so drag order in the picker = display order.
    const existingByClass = new Map<string, FindingLabel>();
    group.findingLabels.forEach((fl) => {
      if (fl.findingClass) existingByClass.set(fl.findingClass, fl);
    });
    const now = new Date().toISOString();
    const nextLabels: FindingLabel[] = next.map((cls, i) => {
      const existing = existingByClass.get(cls);
      if (existing) return { ...existing, index: i };
      return {
        uuid: crypto.randomUUID(),
        label: humanizeClass(cls),
        findingClass: cls,
        index: i,
        createdAt: now,
        updatedAt: now,
      };
    });
    onChange({ ...group, findingLabels: nextLabels });
  };

  return (
    <div className={styles.groupedBlock}>
      <Select
        mode="multiple"
        size="small"
        placeholder="Pick findings"
        value={selectedClasses}
        options={options}
        onChange={handleClassesChange}
        allowClear
        showSearch
        optionFilterProp="label"
        className={styles.groupedSelect}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      />
      <div className={styles.splitRowPlaceholder}>
        <span className={styles.splitRowPlaceholderText}>
          Reader enters text here…
        </span>
      </div>
    </div>
  );
}
